import { getUserData } from '@/api';
import { ActionType, BuyerType, ValidationMessages } from '@/constants';
import router from '@/router';
import store from '@/store';
import { User } from '@/types';
import { filterJobsbyId, hideLoader, setRememberMe, validatePassword } from '.';

const updateLoginState = (path: string) => {
  store.dispatch(ActionType.UPDATE_SIGNIN_STATUS, true);
  router.push({ path }).catch(() => {
    // suppress Vuejs Console Warining when Route override
  });
}

const validate = (newPassword: string, confirmPassword: string, email: string, name = '') => {
  const firstName = name.split(' ').slice(0, -1).join(' ');
  const lastName = name.split(' ').slice(-1).join(' ');

  if (newPassword === confirmPassword) {
    return validatePassword(newPassword, email, firstName, lastName).length ? ValidationMessages.passwordValidation : '';
  } else {
    return ValidationMessages.passwordNotMatch;
  }
}

const validatePasswords = (newPassword: string, confirmPassword: string, email: string, name = '') => {
  if (newPassword && confirmPassword) {
    return validate(
      newPassword,
      confirmPassword,
      email,
      name
    );
  } else {
    return ValidationMessages.requiredFields;
  }
}

/* eslint-disable @typescript-eslint/camelcase */
const getUserAttributes = (attributes: any) => ({
  buyer_contact_id: attributes['custom:contactId'],
  buyer_first_name: attributes['custom:firstName'],
  buyer_last_name: attributes['custom:lastName'],
  cobuyer_first_name: attributes['custom:cobuyerFirstName'],
  cobuyer_last_name: attributes['custom:cobuyerLastName']
});
/* eslint-enable @typescript-eslint/camelcase */

const setUserAttributes = (attributes: any) => {
  localStorage.setItem('drees_customer_portal', JSON.stringify(attributes));
}

const setUserDetails = async (path: string, user?: any) => {
  if (!store.getters.isLoggedInAsAdmin && user) {
    setUserAttributes(getUserAttributes(user.attributes));
  }
  await getUserData();
  hideLoader();
  updateLoginState(path);
}

const setImpersonatedBuyerDetails = (jobId: string, email: string) => {
  setRememberMe(jobId);
  store.dispatch(ActionType.SET_LOGGED_IN_AS_ADMIN, true);
  store.dispatch(ActionType.SET_IMPERSONATED_BUYER, email);
}

const concatMultupleNames = (buyerList: User[], listLength: number) => {
  let nameString = ', ';
  buyerList.forEach((buyer: User, idx: number) => {
    nameString = idx < listLength - 2 ? nameString.concat(`${buyer.firstName}, `) : idx === listLength - 2 ? nameString.concat(`${buyer.firstName} & `) : nameString.concat(buyer.firstName);
  });
  return `${nameString}’s `;
}

const buyerNameGenerator = (buyerList: User[]) => {
  const listLength = buyerList.length;
  if (listLength > 1) {
    return concatMultupleNames(buyerList, listLength);
  } else if (listLength === 1) {
    return ` & ${buyerList[0].firstName}’s `;
  } else {
    return '';
  }
}

const getCoBuyers = (jobId: string): User[] => {
  const otherBuyers = (filterJobsbyId(jobId)?.buyers?.filter((buyer: User) => buyer.email !== store.getters.user.email) || []) as User[];
  const sortedList = otherBuyers.sort((buyer1, buyer2) => {
    return (+buyer1.sortNumber > +buyer2.sortNumber) ? 1 : (+buyer1.sortNumber < +buyer2.sortNumber) ? -1 : 0;
  });
  const buyerIdx = sortedList.findIndex(user => user.buyerType !== BuyerType.COBUYER);
  if (buyerIdx > -1) {
    const buyer = sortedList.splice(buyerIdx, 1)[0];
    sortedList.unshift(buyer);
  }
  return sortedList;
}

const getBuyerNameString = (jobId: string) => filterJobsbyId(jobId)?.buyers?.length > 1 ? `${store.getters.user.firstName}` : `${store.getters.user.firstName}’s `;

const getImpersonationParams = (params = {}) => {
  const { isLoggedInAsAdmin, impersonatedBuyer } = store.getters;
  const impersonatedBuyerEmail = isLoggedInAsAdmin ? { buyeremail: impersonatedBuyer } : {};
  return {
    ...impersonatedBuyerEmail,
    ...params
  }
}

const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

export {
  updateLoginState,
  validatePasswords,
  getUserAttributes,
  setUserAttributes,
  setUserDetails,
  setImpersonatedBuyerDetails,
  buyerNameGenerator,
  getCoBuyers,
  getBuyerNameString,
  getImpersonationParams,
  ordinals
};
